import React from 'react'

import { Formik } from 'formik'

import { AdminContext } from 'contexts/AdminContext'
import { ZariContext } from '_zari/context'

import { Button, Form, Col, Row, Modal, Divider } from '_zari/core'

import {
  ModalidadCalculosDeDepreciacionConstant,
  TiposDeDocumentosRelacionadosCuentaContableConstant
} from 'constants/index'

import { IdGen } from 'utils'

import * as CuentaContableCrud from 'services/cuentaContable.service'

import * as FormGroups from './formGroups'
import { ValidationSchema } from '../_shared/validation'
import { ReparticionesCuentasSection } from '../_shared/sections'

const commonReducer = (state, action) => {
  switch (action.type) {
    case 'agregar': {
      return [
        ...state,
        {
          ...action.payload,
          _idFila: IdGen.newId()
        }
      ]
    }
    case 'eliminar':
      return state.filter(val => val._idFila !== action.payload._idFila)
    default:
      return state
  }
}

const EditarCuentaContableModal = ({ handleClose, show, modalData, reloadTree }) => {
  const __adminContext = React.useContext(AdminContext)
  const __zariContext = React.useContext(ZariContext)

  const [reparticiones, reparticionesDispatcher] = React.useReducer(
    commonReducer,
    modalData.reparticiones,
    innerDetails => {
      if (innerDetails) {
        return innerDetails.map(x => ({
          ...x,
          _idFila: IdGen.newId()
        }))
      } else {
        return []
      }
    }
  )

  const formInitialValues = React.useMemo(() => {
    let flagReparticiones = false
    if (__adminContext.modalidadCalculoDeDepreciacion === ModalidadCalculosDeDepreciacionConstant._6A8_DIST) {
      const cuentaIntValue = parseInt(modalData.prefijoCodigoCuenta.substr(0, 2))
      if ((cuentaIntValue >= 62 && cuentaIntValue <= 67) || cuentaIntValue >= 90) {
        flagReparticiones = true
      }
    }

    return {
      _idCentroCostoNivel1: modalData._idCentroCostoNivel1 ?? '',
      _idCentroCostoNivel2: modalData._idCentroCostoNivel2 ?? '',
      centroCostoNivel2Info: modalData.centroCostoNivel2Info ?? '',
      centroCostoNivel1Info: modalData.centroCostoNivel1Info ?? '',
      descripcion: modalData.descripcion,
      cuentaAutomaticaDebe: modalData.cuentaAutomaticaDebe,
      cuentaAutomaticaHaber: modalData.cuentaAutomaticaHaber,
      nivel: modalData.nivel,
      prefijoCodigoCuenta: modalData.prefijoCodigoCuenta,
      sufijoCodigoCuenta: modalData.sufijoCodigoCuenta,
      tipoDocumento: modalData.tipoDocumento,
      codigoBanco: modalData.codigoBanco,
      cuentaCorrienteBanco: modalData.cuentaCorrienteBanco ?? '',
      tipo: modalData.tipo,
      tipoSocio: modalData.tipoSocio,
      moneda: modalData.moneda,
      descripcionCuentaAutomaticaDebe: modalData.descripcionCuentaAutomaticaDebe,
      descripcionCuentaAutomaticaHaber: modalData.descripcionCuentaAutomaticaHaber,
      flagAnalitica: modalData.flagAnalitica,
      flagCentroCosto: modalData.flagCentroCosto,
      flagDetraccion: modalData.flagDetraccion,
      flagReparticiones: flagReparticiones
    }
  }, [modalData, __adminContext.modalidadCalculoDeDepreciacion])

  const submitHandler = async data => {
    await CuentaContableCrud.actualizarCuentaContable(modalData._id, {
      ...data,
      reparticiones: reparticiones
    })
      .then(() => {
        __zariContext.snack('success', 'El registro se ha agregado.')
        reloadTree()
        handleClose()
      })
      .catch(({ data: { error } }) => __zariContext.snack('error', error))
  }

  return (
    <Modal show={show} onHide={handleClose} size='lg' className='crud_cuenta_contable_modal'>
      <Modal.Header>
        <Modal.Title>EDITAR CUENTA CONTABLE</Modal.Title>
      </Modal.Header>
      <Formik initialValues={formInitialValues} onSubmit={submitHandler} validationSchema={ValidationSchema}>
        {({ errors, handleSubmit, isSubmitting, setValues, setFieldValue, touched, values, handleChange }) => (
          <Form>
            <Modal.Body>
              <Row>
                <Col md={6}>
                  <FormGroups.CodigoCuentaFormGroup
                    fIsSubmitting={true}
                    iError={errors.sufijoCodigoCuenta}
                    iTouched={touched.sufijoCodigoCuenta}
                    vPrefijo={values.prefijoCodigoCuenta}
                  />
                </Col>
                <Col md={6}>
                  <FormGroups.DescripcionFormGroup
                    fIsSubmitting={isSubmitting}
                    iError={errors.descripcion}
                    iTouched={touched.descripcion}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <FormGroups.CuentaAutomaticaDebeFormGroup
                    // fIsSubmitting={isSubmitting}
                    // iError={errors.cuentaAutomaticaDebe}
                    // iTouched={touched.cuentaAutomaticaDebe}
                    iDescripcionName='descripcionCuentaAutomaticaDebe'
                    iLabel='CTA. AUTO. DEBE'
                    iName='cuentaAutomaticaDebe'
                    iError={errors.cuentaAutomaticaDebe}
                    iTouched={touched.cuentaAutomaticaDebe}
                    iValue={values.cuentaAutomaticaDebe}
                    fIsSubmitting={isSubmitting}
                    values={values}
                    setValues={setValues}
                  />
                </Col>
                <Col md={6}>
                  <FormGroups.CuentaAutomaticaHaberFormGroup
                    // fIsSubmitting={isSubmitting}
                    // iError={errors.cuentaAutomaticaHaber}
                    // iTouched={touched.cuentaAutomaticaHaber}
                    iDescripcionName='descripcionCuentaAutomaticaHaber'
                    iLabel='CTA. AUTO. HABER'
                    iName='cuentaAutomaticaHaber'
                    iError={errors.cuentaAutomaticaHaber}
                    iTouched={touched.cuentaAutomaticaHaber}
                    iValue={values.cuentaAutomaticaHaber}
                    fIsSubmitting={isSubmitting}
                    values={values}
                    setValues={setValues}
                  />
                </Col>
              </Row>
              {values.flagReparticiones && (
                <ReparticionesCuentasSection reparticiones={reparticiones} dispatch={reparticionesDispatcher} />
              )}
              <FormGroups.TipoDocumentoFormGroup
                fIsSubmitting={isSubmitting}
                iValue={values.tipoDocumento}
                vPrefijo={values.prefijoCodigoCuenta}
                handleChange={handleChange}
                values={values}
                setValues={setValues}
              />
              <Row>
                <Col md={6}>
                  <FormGroups.TipoFormGroup
                    fIsSubmitting={isSubmitting}
                    iError={errors.tipo}
                    iTouched={touched.tipo}
                    handleChange={handleChange}
                    values={values}
                    setValues={setValues}
                  />
                </Col>
                <Col md={6}>
                  <FormGroups.TipoSocioFormGroup
                    fIsSubmitting={isSubmitting}
                    iError={errors.tipoSocio}
                    iTouched={touched.tipoSocio}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <FormGroups.MonedaFormGroup
                    fIsSubmitting={isSubmitting}
                    iError={errors.moneda}
                    iTouched={touched.moneda}
                    vTipo={values.tipo}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <FormGroups.FlagCentroCostoFormGroup fIsSubmitting={isSubmitting} iValue={values.flagCentroCosto} />
                </Col>
              </Row>
              {values.flagCentroCosto && (
                <Row>
                  <Col md={2}>
                    <FormGroups.CentroCostoNivel1FormGroup
                      iError={errors._idCentroCostoNivel1}
                      iTouched={touched._idCentroCostoNivel1}
                      fIsSubmitting={isSubmitting}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                    />
                  </Col>
                  {values._idCentroCostoNivel1 && (
                    <Col md={2}>
                      <FormGroups.CentroCostoNivel2FormGroup
                        iError={errors._idCentroCostoNivel2}
                        iTouched={touched._idCentroCostoNivel2}
                        idNodoPadre={values._idCentroCostoNivel1}
                        fIsSubmitting={isSubmitting}
                      />
                    </Col>
                  )}
                </Row>
              )}
              {values.tipoDocumento === TiposDeDocumentosRelacionadosCuentaContableConstant.BANCOS && (
                <>
                  <Divider title='Informacion Bancos' />
                  <Row>
                    <Col md={6}>
                      <FormGroups.CodigoBancoFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors.codigoBanco}
                        iTouched={touched.codigoBanco}
                      />
                    </Col>
                    <Col md={6}>
                      <FormGroups.CuentaCorrienteBancoFormGroup
                        fIsSubmitting={isSubmitting}
                        iError={errors.cuentaCorrienteBanco}
                        iTouched={touched.cuentaCorrienteBanco}
                      />
                    </Col>
                  </Row>
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button color='secondary' onClick={handleClose}>
                Cancelar
              </Button>
              <Button disabled={isSubmitting} onClick={handleSubmit}>
                Actualizar
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

export default EditarCuentaContableModal
